import {
  Field,
  IconButton,
  NumberInput,
  SingleSelectDropdown,
  Switch,
  TextInput,
  type SingleSelectDropdownOption,
} from "@carbon6/c6-frontend-design";
import { appTextModes } from "@/shared/ui/text";
import { textOrNull } from "@/shared/utils/text";
import type { InspectChannelDeclaration, PoseDeclaration } from "../../../domain/episodeInspect";
import type { RoleField } from "../../../feature/issuePath";
import {
  ANGLE_UNITS,
  EULER_FRAMES,
  EULER_ORDERS,
  GRIPPER_QUANTITIES,
  HAND_AXES,
  identityHandRotation,
  isIdentityRotation,
  ORIENTATION_KINDS,
  LENGTH_UNITS,
  QUATERNION_ORDERS,
  type Gripper,
  type Axis,
  type HandRole,
  type HandRotation,
  type Mapping,
  type MappingIssue,
  type Orientation,
  type Position,
  type ReferenceTransform,
  type ReferenceTransformKind,
} from "../../../domain/mapping";
import {
  applyDeclaredFrames,
  applyDeclaredOrientation,
  declarationMismatches,
  declaredOrientation,
  declaredPositionUnit,
  derivedZAxis,
  orthogonalAxes,
  referenceQuaternionLength,
  setGripperChannel,
  setHandDegree,
  setHandXAxis,
  setHandYAxis,
  setOrientationChannel,
  setOrientationKind,
  setPositionChannel,
  setReferenceQuaternion,
  setReferenceTransformKind,
  setSelectionColumn,
  type DeclarationField,
} from "../../../feature/mappingEdit";
import {
  GRIPPER_COLUMN_LABELS,
  HAND_AXIS_NAMES,
  orientationColumnLabels,
  POSITION_COLUMN_LABELS,
  REFERENCE_QUATERNION_LABELS,
} from "../../../feature/mappingPath";
import { EvidenceDetails } from "./EvidenceDetails";
import { IssueList } from "./IssueList";
import { SelectionFields } from "./SelectionFields";
import { TcpForm } from "./TcpForm";
import styles from "./MappingForm.module.css";

type RoleUpdate = (update: (role: HandRole) => HandRole) => void;

/** 이 역할에 붙일 검증 issue. 결과가 지금 편집본의 것이 아니면 비어 있다. */
export type RoleIssues = {
  readonly role: readonly MappingIssue[];
  readonly field: (field: RoleField) => readonly MappingIssue[];
  /** 경로 이름을 풀 매핑. */
  readonly mapping: Mapping;
};

type RoleFormProps = {
  /** 생략하면 전체 필드. 기본 검토 화면에서는 선택한 필드만 보인다. */
  field?: RoleField;
  role: HandRole;
  /** 이 episode 의 vector 채널 선언. */
  channels: readonly InspectChannelDeclaration[];
  issues: RoleIssues;
  onChange: RoleUpdate;
};

/** 칸 묶음 안의 검증 결과. 묶음이 이미 어느 칸인지 말하므로 세부 칸 이름만 앞에 붙인다. */
function FieldIssues({
  fields,
  issues,
  label,
}: {
  issues: RoleIssues;
  fields: readonly RoleField[];
  label: string;
}) {
  return (
    <IssueList
      label={`${label} 검증 결과`}
      issues={fields.flatMap((field) => issues.field(field))}
      mapping={issues.mapping}
    />
  );
}

function choices<T extends string>(
  values: readonly T[],
  label: (value: T) => string = (value) => value,
): SingleSelectDropdownOption[] {
  return values.map((value) => ({ value, label: label(value) }));
}

const ORIENTATION_KIND_LABEL: Readonly<Record<Orientation["kind"] & string, string>> = {
  quaternion: "quaternion",
  euler: "Euler",
  matrix: "회전 행렬(행 우선 9 열)",
};

const EULER_FRAME_LABEL: Readonly<Record<(typeof EULER_FRAMES)[number], string>> = {
  intrinsic: "로컬 축(intrinsic)",
  extrinsic: "고정 축(extrinsic)",
};

const QUANTITY_LABEL: Readonly<Record<(typeof GRIPPER_QUANTITIES)[number], string>> = {
  opening_width: "벌림 폭",
  joint_angle: "관절 각",
  encoder: "엔코더",
  normalized: "정규화 값",
};

const DECLARATION_FIELD_LABEL: Readonly<Record<DeclarationField, string>> = {
  reference_frame: "기준 프레임",
  target_frame: "대상 프레임",
  position_unit: "위치 단위",
  rotation: "회전 표현",
};

const DEGREE_STEPS = [-90, -1, 1, 90] as const;

function declarationOf(
  channels: readonly InspectChannelDeclaration[],
  channel: string | null | undefined,
): PoseDeclaration | null {
  return channels.find((item) => item.key === channel)?.pose ?? null;
}

/** 채널이 밝힌 pose 선언 한 줄과 `선언값 사용` 버튼. 선언이 없으면 그 사실만 말한다. */
function DeclarationLine({
  declaration,
  onApply,
}: {
  declaration: PoseDeclaration | null;
  onApply: (() => void) | null;
}) {
  if (declaration === null) {
    return (
      <p className={styles.caption}>이 채널은 좌표계 · 단위 · 회전 표현을 선언하지 않았습니다.</p>
    );
  }
  return (
    <div className={styles.declaration}>
      <span>
        원본 선언: 기준 <code>{declaration.referenceFrame}</code> · 대상{" "}
        <code>{declaration.targetFrame}</code> · 단위 <code>{declaration.positionUnit}</code> · 회전{" "}
        <code>{declaration.rotation}</code>
      </span>
      {onApply === null ? null : (
        <IconButton
          text="선언값 사용"
          size="md"
          type="button"
          variant="surface"
          onClick={onApply}
        />
      )}
    </div>
  );
}

function PositionGroup({ channels, issues, onChange, role }: RoleFormProps) {
  const { position } = role;
  const declaration = declarationOf(channels, position.source.channel);
  const unit = declaration === null ? null : declaredPositionUnit(declaration);
  const patch = (change: (position: Position) => Position): void =>
    onChange((current) => ({ ...current, position: change(current.position) }));
  return (
    <fieldset className={styles.group} data-mapping-field="position">
      <legend>위치</legend>
      <FieldIssues issues={issues} fields={["position"]} label="위치" />
      <SelectionFields
        label="위치 채널"
        selection={position.source}
        channels={channels}
        columnLabels={POSITION_COLUMN_LABELS}
        onChannel={(channel) => patch((current) => setPositionChannel(current, channel))}
        onColumn={(index, column) =>
          patch((current) => ({
            ...current,
            source: setSelectionColumn(current.source, index, column),
          }))
        }
      />
      <Field textVariant={appTextModes["body-normal"]} label="단위" layout="stack">
        <SingleSelectDropdown
          fullWidth
          label="위치 단위"
          options={choices(LENGTH_UNITS)}
          placeholder="단위를 고르세요"
          size="md"
          value={position.unit}
          onValueChange={(value) =>
            patch((current) => ({ ...current, unit: value as typeof position.unit }))
          }
        />
      </Field>
      {position.source.channel === null ? null : (
        <DeclarationLine
          declaration={declaration}
          onApply={
            declaration === null
              ? null
              : () =>
                  onChange((current) => ({
                    ...applyDeclaredFrames(current, declaration),
                    position: unit === null ? current.position : { ...current.position, unit },
                  }))
          }
        />
      )}
    </fieldset>
  );
}

function OrientationGroup({ channels, issues, onChange, role }: RoleFormProps) {
  const { orientation } = role;
  const declaration = declarationOf(channels, orientation.source.channel);
  const declared = declaration === null ? null : declaredOrientation(declaration);
  const patch = (change: (orientation: Orientation) => Orientation): void =>
    onChange((current) => ({ ...current, orientation: change(current.orientation) }));
  return (
    <fieldset className={styles.group} data-mapping-field="orientation">
      <legend>자세</legend>
      <FieldIssues issues={issues} fields={["orientation"]} label="자세" />
      <SelectionFields
        label="자세 채널"
        selection={orientation.source}
        channels={channels}
        columnLabels={orientationColumnLabels(orientation)}
        onChannel={(channel) => patch((current) => setOrientationChannel(current, channel))}
        onColumn={(index, column) =>
          patch((current) => ({
            ...current,
            source: setSelectionColumn(current.source, index, column),
          }))
        }
      >
        <Field textVariant={appTextModes["body-normal"]} label="표현" layout="stack">
          <SingleSelectDropdown
            fullWidth
            label="자세 표현"
            options={choices(ORIENTATION_KINDS, (kind) => ORIENTATION_KIND_LABEL[kind])}
            placeholder="표현을 고르세요"
            size="md"
            value={orientation.kind}
            onValueChange={(value) =>
              patch((current) =>
                setOrientationKind(current, value as (typeof ORIENTATION_KINDS)[number]),
              )
            }
          />
        </Field>
        {orientation.kind === "quaternion" ? (
          <>
            <Field textVariant={appTextModes["body-normal"]} label="성분 순서" layout="stack">
              <SingleSelectDropdown
                fullWidth
                label="quaternion 성분 순서"
                options={choices(QUATERNION_ORDERS, (order) => order.toUpperCase())}
                placeholder="순서를 고르세요"
                size="md"
                value={orientation.quaternion_order}
                onValueChange={(value) =>
                  patch((current) => ({
                    ...current,
                    quaternion_order: value as (typeof QUATERNION_ORDERS)[number],
                  }))
                }
              />
            </Field>
            <Field
              textVariant={appTextModes["body-normal"]}
              label="비단위 quaternion 정규화"
              layout="hug"
              labelPlacement="end"
            >
              <Switch
                checked={orientation.normalize_quaternion}
                onChange={(event) =>
                  patch((current) => ({ ...current, normalize_quaternion: event.target.checked }))
                }
              />
            </Field>
          </>
        ) : null}
        {orientation.kind === "euler" ? (
          <>
            <Field textVariant={appTextModes["body-normal"]} label="축 순서" layout="stack">
              <SingleSelectDropdown
                fullWidth
                label="Euler 축 순서"
                options={choices(EULER_ORDERS)}
                placeholder="순서를 고르세요"
                size="md"
                value={orientation.euler_order}
                onValueChange={(value) =>
                  patch((current) => ({
                    ...current,
                    euler_order: value as (typeof EULER_ORDERS)[number],
                  }))
                }
              />
            </Field>
            <div className={styles.form}>
              <Field textVariant={appTextModes["body-normal"]} label="축 기준" layout="stack">
                <SingleSelectDropdown
                  fullWidth
                  label="Euler 축 기준"
                  options={choices(EULER_FRAMES, (frame) => EULER_FRAME_LABEL[frame])}
                  placeholder="기준"
                  size="md"
                  value={orientation.euler_frame}
                  onValueChange={(value) =>
                    patch((current) => ({
                      ...current,
                      euler_frame: value as (typeof EULER_FRAMES)[number],
                    }))
                  }
                />
              </Field>
              <Field textVariant={appTextModes["body-normal"]} label="각 단위" layout="stack">
                <SingleSelectDropdown
                  fullWidth
                  label="Euler 각 단위"
                  options={choices(ANGLE_UNITS)}
                  placeholder="단위"
                  size="md"
                  value={orientation.angle_unit}
                  onValueChange={(value) =>
                    patch((current) => ({
                      ...current,
                      angle_unit: value as (typeof ANGLE_UNITS)[number],
                    }))
                  }
                />
              </Field>
            </div>
          </>
        ) : null}
      </SelectionFields>
      {orientation.source.channel === null ? null : (
        <DeclarationLine
          declaration={declaration}
          onApply={
            declaration === null
              ? null
              : () =>
                  onChange((current) => ({
                    ...applyDeclaredFrames(current, declaration),
                    orientation:
                      declared === null
                        ? current.orientation
                        : applyDeclaredOrientation(current.orientation, declared),
                  }))
          }
        />
      )}
    </fieldset>
  );
}

/** 빈 칸은 `null` 이다. 숫자로 읽히지 않는 중간 입력(`-` · `.`)도 `null` 로 온다. */
function NumberField({
  label,
  onChange,
  value,
}: {
  label: string;
  value: number | null;
  onChange: (value: number | null) => void;
}) {
  return (
    <Field textVariant={appTextModes["body-normal"]} label={label} layout="stack">
      <NumberInput fullWidth mode="decimal" value={value} onChange={(next) => onChange(next)} />
    </Field>
  );
}

function GripperGroup({ channels, issues, onChange, role }: RoleFormProps) {
  const { gripper } = role;
  const patch = (change: (gripper: Gripper) => Gripper): void =>
    onChange((current) => ({ ...current, gripper: change(current.gripper) }));
  const [clipLow, clipHigh] = gripper.clip_range;
  return (
    <fieldset className={styles.group} data-mapping-field="gripper">
      <legend>그리퍼</legend>
      <FieldIssues issues={issues} fields={["gripper"]} label="그리퍼" />
      <SelectionFields
        label="그리퍼 채널"
        selection={gripper.source}
        channels={channels}
        columnLabels={GRIPPER_COLUMN_LABELS}
        onChannel={(channel) => patch((current) => setGripperChannel(current, channel))}
        onColumn={(index, column) =>
          patch((current) => ({
            ...current,
            source: setSelectionColumn(current.source, index, column),
          }))
        }
      />
      <div className={styles.pair}>
        <Field textVariant={appTextModes["body-normal"]} label="물리량" layout="stack">
          <SingleSelectDropdown
            fullWidth
            label="그리퍼 물리량"
            options={choices(GRIPPER_QUANTITIES, (quantity) => QUANTITY_LABEL[quantity])}
            placeholder="물리량"
            size="md"
            value={gripper.quantity}
            onValueChange={(value) =>
              patch((current) => ({
                ...current,
                quantity: value as (typeof GRIPPER_QUANTITIES)[number],
              }))
            }
          />
        </Field>
        <Field textVariant={appTextModes["body-normal"]} label="원본 단위" layout="stack">
          <TextInput
            fullWidth
            inputSize="md"
            placeholder="예: mm, rad, count"
            value={gripper.unit ?? ""}
            onChange={(event) =>
              patch((current) => ({ ...current, unit: textOrNull(event.target.value) }))
            }
          />
        </Field>
      </div>
      <p className={styles.caption}>
        출력 = (값 − 입력 열림) / (입력 닫힘 − 입력 열림). 출력은 Sigmakit 규약대로 닫힘 1 · 열림 0
        으로 고정입니다.
      </p>
      <div className={styles.pair}>
        <NumberField
          label="입력 닫힘"
          value={gripper.input_closed}
          onChange={(value) => patch((current) => ({ ...current, input_closed: value }))}
        />
        <NumberField
          label="입력 열림"
          value={gripper.input_open}
          onChange={(value) => patch((current) => ({ ...current, input_open: value }))}
        />
      </div>
      <div className={styles.pair}>
        <NumberField
          label="clip 아래"
          value={clipLow}
          onChange={(value) =>
            patch((current) => ({ ...current, clip_range: [value, current.clip_range[1]] }))
          }
        />
        <NumberField
          label="clip 위"
          value={clipHigh}
          onChange={(value) =>
            patch((current) => ({ ...current, clip_range: [current.clip_range[0], value] }))
          }
        />
      </div>
      <p className={styles.caption}>두 칸을 모두 비우면 자르지 않습니다.</p>
    </fieldset>
  );
}

function FramesGroup({ issues, onChange, role }: RoleFormProps) {
  return (
    <fieldset className={styles.group} data-mapping-field="reference_frame target_frame">
      <legend>프레임</legend>
      <FieldIssues issues={issues} fields={["reference_frame", "target_frame"]} label="프레임" />
      <div className={styles.form}>
        <Field textVariant={appTextModes["body-normal"]} label="기준 프레임" layout="stack">
          <TextInput
            fullWidth
            inputSize="md"
            placeholder="예: base"
            value={role.reference_frame ?? ""}
            onChange={(event) =>
              onChange((current) => ({
                ...current,
                reference_frame: textOrNull(event.target.value),
              }))
            }
          />
        </Field>
        <Field textVariant={appTextModes["body-normal"]} label="대상 프레임" layout="stack">
          <TextInput
            fullWidth
            inputSize="md"
            placeholder="예: tool"
            value={role.target_frame ?? ""}
            onChange={(event) =>
              onChange((current) => ({ ...current, target_frame: textOrNull(event.target.value) }))
            }
          />
        </Field>
      </div>
    </fieldset>
  );
}

const REFERENCE_KIND_OPTIONS: SingleSelectDropdownOption[] = [
  { value: "identity", label: "없음 · 원본이 기준 프레임" },
  { value: "fixed", label: "고정 변환" },
];

const TRANSLATION_KEYS = ["x_m", "y_m", "z_m"] as const;

function ReferenceTransformGroup({ issues, onChange, role }: RoleFormProps) {
  const spec = role.reference_transform;
  const patch = (change: (spec: ReferenceTransform) => ReferenceTransform): void =>
    onChange((current) => ({
      ...current,
      reference_transform: change(current.reference_transform),
    }));
  const quaternion = referenceQuaternionLength(spec);
  return (
    <fieldset className={styles.group} data-mapping-field="reference_transform">
      <legend>기준 변환</legend>
      <FieldIssues issues={issues} fields={["reference_transform"]} label="기준 변환" />
      <p className={styles.caption}>
        원본 pose 가 기준 프레임이 아닌 좌표계에 적혀 있으면 고정 변환으로 옮깁니다. 위치는 p = R ·
        p_원본 + t, 자세는 R · R_원본 이고 TCP 이동과 손 축 보정보다 먼저 적용합니다.
      </p>
      <Field textVariant={appTextModes["body-normal"]} label="종류" layout="stack">
        <SingleSelectDropdown
          fullWidth
          label="기준 변환 종류"
          options={REFERENCE_KIND_OPTIONS}
          size="md"
          value={spec.kind}
          onValueChange={(value) =>
            patch((current) => setReferenceTransformKind(current, value as ReferenceTransformKind))
          }
        />
      </Field>
      {spec.kind === "identity" ? null : (
        <>
          <Field textVariant={appTextModes["body-normal"]} label="원본 프레임" layout="stack">
            <TextInput
              fullWidth
              inputSize="md"
              placeholder="예: robot_torso"
              value={spec.source_frame ?? ""}
              onChange={(event) =>
                patch((current) => ({ ...current, source_frame: textOrNull(event.target.value) }))
              }
            />
          </Field>
          <p className={styles.caption}>
            R 은 Hamilton quaternion(w · x · y · z)이고 원본 프레임의 벡터를 기준 프레임으로
            옮깁니다. t 는 기준 프레임 축으로 적은 원본 프레임 원점의 위치(m)입니다. 모르는 값은
            비워 둡니다.
          </p>
          <div className={styles.pair}>
            {REFERENCE_QUATERNION_LABELS.map((name, index) => (
              <NumberField
                key={name}
                label={`quaternion ${name}`}
                value={spec.quaternion_wxyz[index]}
                onChange={(value) =>
                  patch((current) => setReferenceQuaternion(current, index as 0 | 1 | 2 | 3, value))
                }
              />
            ))}
          </div>
          {quaternion === null ? null : quaternion.unit ? (
            <p className={styles.caption}>quaternion 길이 {quaternion.length.toFixed(6)}</p>
          ) : (
            <p className={styles.attention} role="status">
              quaternion 길이가 {quaternion.length.toFixed(6)} 입니다. 1 ± 0.000001 이어야 하고
              조용히 정규화하지 않으니 네 성분을 확인해 주세요.
            </p>
          )}
          <div className={styles.pair}>
            {TRANSLATION_KEYS.map((key, index) => (
              <NumberField
                key={key}
                label={`이동 ${POSITION_COLUMN_LABELS[index]} (m)`}
                value={spec[key]}
                onChange={(value) => patch((current) => ({ ...current, [key]: value }))}
              />
            ))}
          </div>
          <EvidenceDetails label="기준 변환" evidence={spec.evidence} />
        </>
      )}
    </fieldset>
  );
}

function HandAxesGroup({ issues, onChange, role }: RoleFormProps) {
  const { rotation } = role;
  const { x_axis: xAxis, y_axis: yAxis, degrees } = rotation;
  const setRotation = (change: (current: HandRotation) => HandRotation): void =>
    onChange((current) => ({ ...current, rotation: change(current.rotation) }));
  return (
    <fieldset className={styles.group} data-mapping-field="rotation">
      <legend>손 축</legend>
      <FieldIssues issues={issues} fields={["rotation"]} label="손 축" />
      <p className={styles.caption}>
        원본 자세의 로컬 축에서 새 손 X · Y 축을 고릅니다. Z 는 X × Y 입니다. 추가 각도는 새 손 로컬
        축 기준으로 X → Y → Z 순서로 적용합니다. 위치는 바뀌지 않습니다.
      </p>
      <div className={styles.pair}>
        <Field textVariant={appTextModes["body-normal"]} label="손 X 축" layout="stack">
          <SingleSelectDropdown
            fullWidth
            label="손 X 축"
            options={choices(HAND_AXES)}
            size="md"
            value={xAxis}
            onValueChange={(value) =>
              setRotation((current) => setHandXAxis(current, value as Axis))
            }
          />
        </Field>
        <Field textVariant={appTextModes["body-normal"]} label="손 Y 축" layout="stack">
          <SingleSelectDropdown
            fullWidth
            label="손 Y 축"
            options={choices(orthogonalAxes(xAxis))}
            size="md"
            value={yAxis}
            onValueChange={(value) =>
              setRotation((current) => setHandYAxis(current, value as Axis))
            }
          />
        </Field>
      </div>
      <p className={styles.axisFacts}>Z = X × Y = {derivedZAxis(xAxis, yAxis) ?? "—"}</p>
      {HAND_AXIS_NAMES.map((axis, index) => (
        <div key={axis} className={styles.degreeRow}>
          <span className={styles.caption}>{axis} 각</span>
          <NumberInput
            aria-label={`손 ${axis} 추가 각도(도)`}
            fullWidth
            inputSize="md"
            mode="decimal"
            value={degrees[index]}
            onChange={(value) => {
              if (value !== null) {
                setRotation((current) => setHandDegree(current, index as 0 | 1 | 2, value));
              }
            }}
          />
          <div className={styles.degreeButtons}>
            {DEGREE_STEPS.map((step) => (
              <IconButton
                key={step}
                aria-label={`손 ${axis} 각 ${step > 0 ? "+" : ""}${step}도`}
                size="xs"
                text={`${step > 0 ? "+" : ""}${step}°`}
                type="button"
                variant="surface"
                onClick={() =>
                  setRotation((current) =>
                    setHandDegree(current, index as 0 | 1 | 2, current.degrees[index] + step),
                  )
                }
              />
            ))}
          </div>
        </div>
      ))}
      {isIdentityRotation(rotation) ? (
        <p className={styles.caption}>보정이 없습니다. 원본 자세를 그대로 씁니다.</p>
      ) : (
        <IconButton
          text="보정 지우기"
          size="md"
          type="button"
          variant="surface"
          onClick={() => setRotation(identityHandRotation)}
        />
      )}
    </fieldset>
  );
}

function ReasonGroup({ channels, issues, onChange, role }: RoleFormProps) {
  const declarations = new Map(channels.map((channel) => [channel.key, channel.pose]));
  const mismatches = declarationMismatches(role, declarations);
  const needsReason = mismatches.length > 0 && role.reinterpret_reason === null;
  return (
    <fieldset className={styles.group} data-mapping-field="reinterpret_reason">
      <legend>재해석 사유</legend>
      <FieldIssues issues={issues} fields={["reinterpret_reason"]} label="재해석 사유" />
      {mismatches.length === 0 ? null : (
        <p className={styles.attention} role="status">
          원본 선언과 다른 칸이 있습니다:{" "}
          {mismatches
            .map((field) =>
              field === "reference_frame" && role.reference_transform.kind === "fixed"
                ? "원본 프레임"
                : DECLARATION_FIELD_LABEL[field],
            )
            .join(" · ")}
          .{needsReason ? " 이유를 적지 않으면 검증이 오류로 봅니다." : " 검증은 경고로 봅니다."}
        </p>
      )}
      <Field textVariant={appTextModes["body-normal"]} label="이유" layout="stack">
        <TextInput
          fullWidth
          inputSize="md"
          invalid={needsReason}
          placeholder="원본 선언과 다르게 해석한 이유"
          value={role.reinterpret_reason ?? ""}
          onChange={(event) =>
            onChange((current) => ({
              ...current,
              reinterpret_reason: textOrNull(event.target.value),
            }))
          }
        />
      </Field>
    </fieldset>
  );
}

/** 손 하나의 관측 또는 action 폼. 칸을 바꿀 때마다 편집본이 바뀐다. 저장은 하지 않는다. */
export function RoleForm(props: RoleFormProps) {
  const selected = (fields: readonly RoleField[]) =>
    props.field === undefined || fields.includes(props.field);
  return (
    <div className={styles.form}>
      <IssueList label="역할 검증 결과" issues={props.issues.role} mapping={props.issues.mapping} />
      {selected(["position"]) ? <PositionGroup {...props} /> : null}
      {selected(["orientation"]) ? <OrientationGroup {...props} /> : null}
      {selected(["gripper"]) ? <GripperGroup {...props} /> : null}
      {selected(["reference_frame", "target_frame"]) ? <FramesGroup {...props} /> : null}
      {selected(["reference_transform"]) ? <ReferenceTransformGroup {...props} /> : null}
      {selected(["tcp", "pose_kind"]) ? (
        <>
          <FieldIssues issues={props.issues} fields={["tcp", "pose_kind"]} label="TCP" />
          <TcpForm role={props.role} onChange={props.onChange} />
        </>
      ) : null}
      {selected(["rotation"]) ? <HandAxesGroup {...props} /> : null}
      {selected(["reinterpret_reason"]) ? <ReasonGroup {...props} /> : null}
    </div>
  );
}
